import { Box } from '@mui/material';
import { toSxArray } from 'src/utils/sx';
import { PageScaffoldProps } from './page-scaffold.types';

/**
 * Every page needs the same vertical behaviour — the scrollbar must hug the
 * page edge and run the full height while the title/tabs stay pinned. Centralising
 * that here (rather than re-implementing `position: sticky` + an `overflow`
 * container on each page) keeps the scroll/sticky contract identical everywhere
 * and lets it stay decoupled from horizontal width (handled by PageContainer).
 *
 * Owns the scroll container and the sticky-header zone: the scrollbar sits at
 * the edge of this region and runs its full height, while `header` stays pinned
 * to the top as the body scrolls beneath it.
 *
 * Horizontal width is intentionally NOT handled here — wrap `header` and
 * `children` in `PageContainer.Centered` / `PageContainer.Fluid` (or use the
 * `PageLayout` convenience wrapper that does this for you).
 */
export const PageScaffold = ({
  header,
  children,
  headerZIndex = 1,
  sx,
  ...rest
}: PageScaffoldProps) => (
  <Box
    sx={[
      {
        height: '100%',
        overflowY: 'auto',
        // Disable the rubber-band/bounce at the scroll extremes (and stop the
        // page scroll from chaining to ancestors) so the page feels like an
        // app surface rather than a document.
        overscrollBehavior: 'none',
        // Isolate the sticky header's stacking context to THIS container. Without
        // it, `headerZIndex` competes in the root context and can paint over app
        // chrome (the nav drawer, modals, tooltips). Isolated, the header only
        // stacks above the page body, while overlays always sit above the page.
        isolation: 'isolate',
        backgroundColor: (theme) => theme.palette.custom.OV.sys.background,
      },
      ...toSxArray(sx),
    ]}
    {...rest}
  >
    {header ? (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: headerZIndex,
          backgroundColor: (theme) => theme.palette.custom.OV.sys.background,
        }}
      >
        {header}
      </Box>
    ) : null}
    <Box>{children}</Box>
  </Box>
);

PageScaffold.displayName = 'PageScaffold';
